import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { campaignTypeDefinition, campaignTypeFromStored, type CampaignType } from "../../src/domain/campaignTypes";
import { apiFetch } from "../api";
import { OnboardingChecklist } from "../components/OnboardingChecklist";

type DashboardCampaign = {
  id: string;
  name: string;
  status: "draft" | "active";
  campaignType?: CampaignType;
  trigger?: "pre_checkout" | "after_add" | "product_page";
  updatedAt: string;
};

export function Dashboard() {
  const [campaigns, setCampaigns] = useState<DashboardCampaign[]>([]);
  const [appEmbedEnabled, setAppEmbedEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiFetch("/api/campaigns").then((response) => (response.ok ? response.json() : { campaigns: [] })),
      apiFetch("/api/onboarding").then((response) => (response.ok ? response.json() : { items: [] })),
    ])
      .then(([campaignPayload, onboardingPayload]) => {
        if (cancelled) return;
        setCampaigns((campaignPayload as { campaigns?: DashboardCampaign[] }).campaigns ?? []);
        const embed = (onboardingPayload as { items?: Array<{ id: string; completed: boolean }> }).items?.find(
          (item) => item.id === "enable-app-embed",
        );
        setAppEmbedEnabled(embed?.completed ?? false);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const activeCampaigns = useMemo(() => campaigns.filter((campaign) => campaign.status === "active"), [campaigns]);
  const afterAddActive = activeCampaigns.some((campaign) => campaignTypeFromStored(campaign) === "after_add_products");
  const productPageActive = activeCampaigns.some((campaign) => campaignTypeFromStored(campaign) === "product_page_addons");
  const preCheckoutActive = activeCampaigns.some((campaign) => campaignTypeFromStored(campaign) === "pre_checkout_upsell");

  return (
    <s-page heading="Dashboard">
      <header className="page-header">
        <div>
          <s-paragraph>Launch and manage every offer from one place.</s-paragraph>
        </div>
        <Link className="button primary" to="/campaigns/new">Create campaign</Link>
      </header>

      <section className="dashboard-status-grid" aria-label="App status">
        <StatusCard label="App embed" active={appEmbedEnabled} activeText="Connected to theme" inactiveText="Enable in your theme" />
        <StatusCard label="After add to cart" active={afterAddActive} activeText="Active campaign" inactiveText="No active campaign" />
        <StatusCard label="Product page add-ons" active={productPageActive} activeText="Active campaign" inactiveText="No active campaign" />
        <StatusCard label="Before checkout" active={preCheckoutActive} activeText="Active campaign" inactiveText="No active campaign" />
      </section>

      <section className="dashboard-summary-grid" aria-label="Campaign summary">
        <Summary label="Campaigns" value={campaigns.length} />
        <Summary label="Active" value={activeCampaigns.length} />
        <Summary label="Drafts" value={campaigns.length - activeCampaigns.length} />
        <Summary label="Placements in use" value={Number(afterAddActive) + Number(productPageActive) + Number(preCheckoutActive)} />
      </section>

      <div className="dashboard-primary-grid">
        <OnboardingChecklist />
        <s-section heading="Recent campaigns">
          {campaigns.length ? (
            <div className="table-scroll">
              <table className="campaign-table compact-table">
                <thead><tr><th>Campaign</th><th>Placement</th><th>Status</th></tr></thead>
                <tbody>
                  {campaigns.slice(0, 5).map((campaign) => {
                    const definition = campaignTypeDefinition(campaignTypeFromStored(campaign));
                    return (
                      <tr key={campaign.id}>
                        <td><Link to={`/campaigns/${campaign.id}`}>{campaign.name}</Link></td>
                        <td>{definition.placementLabel}</td>
                        <td><span className={`status-pill ${campaign.status}`}>{campaign.status}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="dashboard-empty">
              <span aria-hidden="true">＋</span>
              <strong>Create your first campaign</strong>
              <small>Choose a placement and configure the products customers should see.</small>
              <Link className="polaris-button" to="/campaigns/new">Create campaign</Link>
            </div>
          )}
          {campaigns.length ? <Link className="section-link" to="/campaigns">View all campaigns →</Link> : null}
        </s-section>
      </div>
    </s-page>
  );
}

function StatusCard({ label, active, activeText, inactiveText }: { label: string; active: boolean; activeText: string; inactiveText: string }) {
  return (
    <article className="status-card" data-active={active}>
      <span className="status-indicator" aria-hidden="true">{active ? "✓" : "!"}</span>
      <span><strong>{label}</strong><small>{active ? activeText : inactiveText}</small></span>
      <span className={`status-pill ${active ? "active" : "draft"}`}>{active ? "Active" : "Setup"}</span>
    </article>
  );
}

function Summary({ label, value }: { label: string; value: number }) {
  return <article className="summary-card"><span>{label}</span><strong>{value}</strong></article>;
}
